import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories, manhwaList } from '@/mocks/manhwa';

function getCategoryBySlug(slug: string) {
  return categories.find((c) => c.slug === slug) || categories[0];
}

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug);
  const [activeCategory, setActiveCategory] = useState(slug);

  const filteredManhwa = useMemo(() => {
    if (activeCategory === 'all') return manhwaList;
    if (activeCategory === 'hot') return manhwaList.filter((m) => m.status === '连载中').sort((a, b) => b.views - a.views);
    if (activeCategory === 'completed') return manhwaList.filter((m) => m.status === '已完成');
    return manhwaList.filter((m) => m.category === activeCategory);
  }, [activeCategory]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: category.name },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 韩漫星球`,
    description: category.description,
    url: `http://911hl.cn/category/${slug}`,
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫星球',
    url: 'http://911hl.cn/',
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 韩漫免费在线阅读 | 韩漫星球`}
        description={category.description}
        keywords={`韩国漫画,韩漫免费阅读,${category.name},下拉式韩漫,韩漫在线`}
        canonicalPath={`/category/${slug}`}
        schemaJson={[orgSchema, categorySchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <div className="pt-20 pb-10 md:pb-14 px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-4">
                <Breadcrumb items={breadcrumbItems} />
              </div>

              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                  {category.name}
                </h1>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  {category.description}
                </p>
              </div>

              <div className="flex items-center justify-center flex-wrap gap-2 mb-10">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredManhwa.map((manhwa) => (
                  <Link
                    key={manhwa.id}
                    to={`/article/${manhwa.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={manhwa.coverImage}
                        alt={manhwa.title}
                        title={`${manhwa.title} - 韩国漫画免费在线`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      {manhwa.status === '连载中' && (
                        <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
                          连载
                        </span>
                      )}
                      {manhwa.status === '已完成' && (
                        <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
                          完结
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {manhwa.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs text-foreground-500">{manhwa.author}</span>
                      <span className="text-foreground-400">·</span>
                      <span className="text-xs text-accent-500 flex items-center gap-1">
                        <i className="ri-star-fill text-[10px]"></i>
                        {manhwa.rating}
                      </span>
                    </div>
                  </Link>
                ))}

                {filteredManhwa.length === 0 && (
                  <div className="col-span-full text-center py-20">
                    <i className="ri-inbox-line text-4xl text-foreground-300 mb-4 block"></i>
                    <p className="text-foreground-500">该分类暂无漫画作品</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 mb-10">
                <div className="lg:w-1/3">
                  <span className="inline-block px-4 py-1.5 rounded-full border border-primary-300 text-primary-600 text-sm mb-4 whitespace-nowrap">
                    平台数据
                  </span>
                </div>
                <div className="lg:w-2/3">
                  <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
                    韩漫星球自成立以来，始终致力于为中文读者提供最全面、最优质的韩国漫画在线免费阅读服务。我们的漫画库涵盖恋爱、奇幻、悬疑、都市、历史等多种类型，所有作品均支持下拉式阅读，完美适配各种设备。我们与多家韩国漫画平台保持紧密合作，确保第一时间同步更新最新章节。
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                <div className="bg-background-50 p-6 rounded-lg text-center">
                  <p className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">3200<strong className="text-primary-500">+</strong></p>
                  <p className="text-sm text-foreground-600">收录韩漫</p>
                </div>
                <div className="bg-background-50 p-6 rounded-lg text-center">
                  <p className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">48<strong className="text-primary-500">部</strong></p>
                  <p className="text-sm text-foreground-600">本周更新</p>
                </div>
                <div className="bg-background-50 p-6 rounded-lg text-center">
                  <p className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">99.8<strong className="text-primary-500">%</strong></p>
                  <p className="text-sm text-foreground-600">读者好评率</p>
                </div>
                <div className="bg-background-50 p-6 rounded-lg text-center">
                  <p className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">5.2<strong className="text-primary-500">亿</strong></p>
                  <p className="text-sm text-foreground-600">累计阅读量</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}